import { useEffect, useMemo, useState } from "react";

import { TruckIcon } from "@heroicons/react/24/outline";

import Modal from "../../layouts/Modal";
import useMvlSearch from "../../data/useMvl";
import { IAmazonMvl } from "../../models/mvl/IAmazonMvl";
import { IVehicle } from "../../models/Vehicle";
import { ButtonInput } from "./Inputs/ButtonInput";

interface IKTypeLookupModalProps {
  open: boolean;
  onClose: () => void;
  initialMake?: string | null;
  initialModel?: string | null;
  vehicle?: IVehicle | null;
  onSelect: (kType: number, row: IAmazonMvl) => void;
}

type VehicleInfoField = { label: string; value: string };

function buildVehicleInfoFields(vehicle: IVehicle): VehicleInfoField[] {
  const engineSize =
    typeof vehicle.combinedEngineCapacity === "number"
      ? `${vehicle.combinedEngineCapacity}cc`
      : typeof vehicle.engineCapacity === "number"
        ? `${vehicle.engineCapacity}cc`
        : null;

  const fields: Array<{ label: string; value: string | null | undefined }> = [
    { label: "Reg", value: vehicle.vrm },
    { label: "Year", value: vehicle.dvlaYearOfManufacture?.toString() },
    { label: "Make", value: vehicle.combinedMake || vehicle.dvlaMake },
    { label: "Model", value: vehicle.combinedModel || vehicle.dvlaModel },
    { label: "Variant", value: vehicle.modelVariantDescription },
    { label: "Body", value: vehicle.bodyStyleDescription || vehicle.bodyStyle },
    { label: "Fuel", value: vehicle.combinedFuelType || vehicle.fuelType || vehicle.fuelDescription },
    { label: "Engine", value: [engineSize, vehicle.engineModelCode].filter(Boolean).join(" · ") || null },
    {
      label: "Gearbox",
      value: vehicle.combinedTransmission || vehicle.dvlaTransmission || vehicle.transmission,
    },
    {
      label: "Doors",
      value:
        typeof vehicle.numberOfDoors === "number" && vehicle.numberOfDoors > 0
          ? vehicle.numberOfDoors.toString()
          : null,
    },
    { label: "KType", value: vehicle.kType?.toString() },
  ];

  return fields
    .filter((f): f is { label: string; value: string } => !!f.value && String(f.value).trim().length > 0)
    .map((f) => ({ label: f.label, value: String(f.value) }));
}

// Search the Amazon Automotive GB MVL (via Betty) and pick a KType for a vehicle.
export default function KTypeLookupModal({
  open,
  onClose,
  initialMake,
  initialModel,
  vehicle,
  onSelect,
}: IKTypeLookupModalProps) {
  const [make, setMake] = useState(initialMake ?? "");
  const [model, setModel] = useState(initialModel ?? "");
  const [query, setQuery] = useState("");
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (!open) return;
    setMake(initialMake ?? "");
    setModel(initialModel ?? "");
    setQuery("");
    setSubmitted(!!(initialMake || initialModel));
  }, [open, initialMake, initialModel]);

  const { search, results } = useMvlSearch(
    {
      make: make.trim() || undefined,
      model: model.trim() || undefined,
      query: query.trim() || undefined,
      take: 50,
    },
    open && submitted
  );

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
  };

  const vehicleFields = useMemo(
    () => (vehicle ? buildVehicleInfoFields(vehicle) : []),
    [vehicle]
  );

  return (
    <Modal open={open} setOpen={onClose} width="max-w-4xl">
      <div className="space-y-4">
        <div>
          <h2 className="text-lg font-medium text-gray-900">Lookup KType (Amazon MVL)</h2>
          <p className="mt-1 text-sm text-gray-600">
            Search the Amazon Automotive GB Master Vehicle List by make, model or free text, then pick a row to set the vehicle KType.
          </p>
        </div>

        {vehicleFields.length > 0 && (
          <div className="rounded-lg border border-slate-200 bg-slate-50/80 px-3 py-2.5">
            <div className="mb-2 flex items-center gap-1.5 text-slate-600">
              <TruckIcon className="h-3.5 w-3.5 shrink-0" aria-hidden />
              <p className="text-[11px] font-semibold uppercase tracking-wide">Vehicle on file</p>
            </div>
            <dl className="grid grid-cols-2 gap-x-4 gap-y-1.5 sm:grid-cols-3 md:grid-cols-4">
              {vehicleFields.map((field) => (
                <div key={field.label} className="min-w-0">
                  <dt className="text-[10px] font-medium uppercase tracking-wide text-slate-500">
                    {field.label}
                  </dt>
                  <dd className="truncate text-sm font-medium text-slate-900" title={field.value}>
                    {field.value}
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        )}

        <form onSubmit={handleSearch} className="grid grid-cols-1 gap-3 sm:grid-cols-4">
          <label className="text-sm sm:col-span-1">
            <span className="font-medium text-gray-500">Make</span>
            <input
              className="mt-1 w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
              value={make}
              onChange={(e) => setMake(e.target.value)}
            />
          </label>
          <label className="text-sm sm:col-span-1">
            <span className="font-medium text-gray-500">Model</span>
            <input
              className="mt-1 w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
              value={model}
              onChange={(e) => setModel(e.target.value)}
            />
          </label>
          <label className="text-sm sm:col-span-1">
            <span className="font-medium text-gray-500">Query</span>
            <input
              className="mt-1 w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Variant, ktype…"
            />
          </label>
          <div className="flex items-end">
            <ButtonInput
              label={search.isFetching ? "Searching…" : "Search"}
              isSubmit={true}
              onClick={undefined}
              classes={"w-full"}
              isLoading={search.isFetching}
            />
          </div>
        </form>

        <div className="max-h-96 overflow-auto rounded border border-gray-200">
          {search.isFetching ? (
            <p className="p-6 text-center text-sm text-gray-500">Searching…</p>
          ) : !submitted ? (
            <p className="p-6 text-center text-sm text-gray-500">Enter a make/model and search.</p>
          ) : results.length === 0 ? (
            <p className="p-6 text-center text-sm text-gray-500">No matching vehicles found.</p>
          ) : (
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="sticky top-0 bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium uppercase text-gray-500">KType</th>
                  <th className="px-3 py-2 text-left text-xs font-medium uppercase text-gray-500">Make</th>
                  <th className="px-3 py-2 text-left text-xs font-medium uppercase text-gray-500">Model</th>
                  <th className="px-3 py-2 text-left text-xs font-medium uppercase text-gray-500">Variant</th>
                  <th className="px-3 py-2 text-left text-xs font-medium uppercase text-gray-500">Body</th>
                  <th className="px-3 py-2 text-left text-xs font-medium uppercase text-gray-500">Energy</th>
                  <th className="px-3 py-2 text-left text-xs font-medium uppercase text-gray-500">Years</th>
                  <th className="px-3 py-2" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {results.map((row) => (
                  <tr key={row.id}>
                    <td className="px-3 py-2 font-semibold text-gray-900">{row.kType ?? "—"}</td>
                    <td className="px-3 py-2 text-gray-700">{row.make ?? "—"}</td>
                    <td className="px-3 py-2 text-gray-700">{row.model ?? "—"}</td>
                    <td className="px-3 py-2 text-gray-700">{row.variantName ?? "—"}</td>
                    <td className="px-3 py-2 text-gray-700">{row.bodyStyle ?? "—"}</td>
                    <td className="px-3 py-2 text-gray-700">{row.energy ?? "—"}</td>
                    <td className="px-3 py-2 text-gray-700">
                      {[row.productStartMonthYear, row.productEndMonthYear].filter(Boolean).join(" – ") || "—"}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <button
                        type="button"
                        disabled={!row.kType}
                        onClick={() => row.kType != null && onSelect(row.kType, row)}
                        className="rounded-md bg-primary-600 px-2.5 py-1 text-xs font-semibold text-white hover:bg-primary-500 disabled:opacity-50"
                      >
                        Use
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="flex justify-end">
          <ButtonInput
            label={"Close"}
            isSubmit={false}
            onClick={onClose}
            classes={"!bg-gray-200 hover:!bg-gray-300 !text-gray-700"}
          />
        </div>
      </div>
    </Modal>
  );
}
